/**
 * Nova automation DSL: fixed JSON Schema + Zod validator + policy enforcement.
 *
 * This is the server-side gate between the LLM planner and persistence. The
 * planner (AgentAiService.generateStructured) emits a `NovaAutomationPlanDraft`
 * as JSON; the service attaches `metadata` and runs `validateNovaPlan` here.
 *
 * The validator enforces THREE things beyond structural shape:
 *   1. SAFETY: every SIDE-EFFECTING step MUST be `dry_run: true` +
 *      `requires_approval: true` (draft, never submit). Side-effecting is
 *      DEFAULT-DENY: a step is side-effecting unless it is on the read-only
 *      allow-list, and EVERY `connector.action` step is side-effecting.
 *   2. CATALOG BINDING: `connector.action` steps (and `event` trigger sources)
 *      must reference a connector/action that exists in the LIVE catalog passed
 *      in, so the planner can never invent a service it cannot reach.
 *   3. LIMITS: `max_runs_per_day` / `max_concurrent_runs` within bounds; step
 *      count bounded; `depends_on` references resolve with no cycle.
 *
 * Zero runtime deps beyond zod (matches the rest of @tabbio/utils).
 */
import { z } from 'zod';
import type {
  NovaAutomation,
  NovaStep,
  NovaTriggerType,
} from '../../../types/src/automation';

export const NOVA_PLAN_SCHEMA_VERSION = 1 as const;

// ── Bounds (TUNABLE: abuse ceilings, not product limits) ──────────────────────

export const NOVA_LIMITS_BOUNDS = {
  maxRunsPerDay: { min: 1, max: 50 },
  maxConcurrentRuns: { min: 1, max: 5 },
} as const;

/** Max steps in a single automation (planner output ceiling). */
export const NOVA_MAX_STEPS = 25;

// ── Side-effect classification (DEFAULT-DENY) ──────────────────────────────────
//
// A step is treated as SIDE-EFFECTING unless its type is on this read-only
// allow-list. Every `connector.action` step is side-effecting (it reaches an
// external provider). This default-deny posture means a mislabeled write step is
// still forced into dry_run + requires_approval; only known-pure reads opt out.

export const NOVA_READ_ONLY_STEP_TYPES: ReadonlySet<string> = new Set([
  'nova.fetch_profile',
  'nova.fetch_cv',
  'nova.fetch_applications',
  'nova.search_jobs',
  'nova.evaluate_matches',
  'nova.summarize',
  'nova.compose_digest',
  'nova.get_views',
  'nova.get_analytics',
  // Research and report steps read public pages and the user's own files and
  // write only artifacts the user owns. Nothing leaves Tabbio for a third
  // party, so they run without an approval pause.
  'nova.research_web',
  'nova.read_sources',
  'nova.compose_report',
  'nova.render_document',
  'nova.deliver',
  'tabbio.fetch_profile',
  'tabbio.fetch_cv',
  'tabbio.fetch_applications',
  'tabbio.search_jobs',
  'tabbio.get_analytics',
  'tabbio.get_views',
]);

/** The literal connector-step type. Always side-effecting + catalog-bound. */
export const NOVA_CONNECTOR_STEP_TYPE = 'connector.action';

// ── Research and report steps (Tasks parity) ───────────────────────────────────

export const NOVA_REPORT_STEP_TYPES = {
  researchWeb: 'nova.research_web',
  readSources: 'nova.read_sources',
  composeReport: 'nova.compose_report',
  renderDocument: 'nova.render_document',
  deliver: 'nova.deliver',
} as const;

/** Bounds on research and report step params, and the per-run call caps. */
export const NOVA_REPORT_LIMITS = {
  topicChars: 200,
  regionChars: 80,
  maxSources: 8,
  readUrls: 5,
  readAssets: 3,
  titleChars: 160,
  toneChars: 80,
  skillIdChars: 200,
  githubChars: 300,
  urlChars: 2048,
  /** Research and read calls one run may make (search, page, PDF, repository, skill). */
  researchCallsPerRun: 3,
  /** Images one run may generate. */
  imagesPerRun: 1,
} as const;

const RL = NOVA_REPORT_LIMITS;

const httpUrlString = z
  .string()
  .trim()
  .min(1)
  .max(RL.urlChars)
  .refine((value) => {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' || url.protocol === 'http:';
    } catch {
      return false;
    }
  }, 'Links must be http or https URLs.');

const githubRepoString = z
  .string()
  .trim()
  .min(1)
  .max(RL.githubChars)
  .refine(
    (value) => /^https?:\/\/(www\.)?github\.com\/[^/\s]+\/[^/\s]+/i.test(value),
    'Use a public GitHub repository link such as https://github.com/owner/repo.',
  );

const researchWebParamsSchema = z.object({
  topic: z.string().trim().min(1).max(RL.topicChars),
  region: z.string().trim().max(RL.regionChars).nullish(),
  time_window: z.enum(['day', 'week', 'month']),
  max_sources: z.number().int().min(1).max(RL.maxSources),
});

const readSourcesParamsSchema = z
  .object({
    urls: z.array(httpUrlString).min(1).max(RL.readUrls).optional(),
    github: githubRepoString.optional(),
    asset_ids: z
      .array(z.string().trim().min(1).max(64))
      .min(1)
      .max(RL.readAssets)
      .optional(),
    skill_id: z.string().trim().min(1).max(RL.skillIdChars).optional(),
  })
  .refine(
    (value) =>
      Boolean(
        value.urls?.length ||
          value.github ||
          value.asset_ids?.length ||
          value.skill_id,
      ),
    'Name at least one source: urls, github, asset_ids or skill_id.',
  );

const composeReportParamsSchema = z.object({
  format: z.enum(['summary', 'document', 'slides']),
  title: z.string().trim().min(1).max(RL.titleChars),
  tone: z.string().trim().max(RL.toneChars).nullish(),
  include_image: z.boolean(),
});

const renderDocumentParamsSchema = z.object({
  formats: z
    .array(z.enum(['html', 'pdf', 'pptx']))
    .min(1)
    .max(3)
    .refine(
      (formats) => new Set(formats).size === formats.length,
      'Each format may appear once.',
    ),
});

const deliverParamsSchema = z.object({
  channels: z
    .array(z.enum(['in_app', 'email']))
    .min(1)
    .max(2)
    .refine(
      (channels) => new Set(channels).size === channels.length,
      'Each channel may appear once.',
    ),
});

export type NovaResearchWebParams = z.infer<typeof researchWebParamsSchema>;
export type NovaReadSourcesParams = z.infer<typeof readSourcesParamsSchema>;
export type NovaComposeReportParams = z.infer<typeof composeReportParamsSchema>;
export type NovaRenderDocumentParams = z.infer<
  typeof renderDocumentParamsSchema
>;
export type NovaDeliverParams = z.infer<typeof deliverParamsSchema>;

export type NovaReportStepParams =
  | { type: 'nova.research_web'; params: NovaResearchWebParams }
  | { type: 'nova.read_sources'; params: NovaReadSourcesParams }
  | { type: 'nova.compose_report'; params: NovaComposeReportParams }
  | { type: 'nova.render_document'; params: NovaRenderDocumentParams }
  | { type: 'nova.deliver'; params: NovaDeliverParams };

const REPORT_PARAM_SCHEMAS: Record<string, z.ZodTypeAny> = {
  'nova.research_web': researchWebParamsSchema,
  'nova.read_sources': readSourcesParamsSchema,
  'nova.compose_report': composeReportParamsSchema,
  'nova.render_document': renderDocumentParamsSchema,
  'nova.deliver': deliverParamsSchema,
};

/** True for the five research and report step types. */
export function isNovaReportStepType(type: string): boolean {
  return type in REPORT_PARAM_SCHEMAS;
}

/**
 * Parse one research or report step's params. Returns the typed params, or
 * the first problem in plain words. The validator and the executor both read
 * params through this, so a stored plan can never drift from what runs.
 */
export function readNovaReportStepParams(
  type: string,
  params: unknown,
):
  | { ok: true; value: NovaReportStepParams }
  | { ok: false; path: string; message: string } {
  const schema = REPORT_PARAM_SCHEMAS[type];
  if (!schema) {
    return { ok: false, path: 'type', message: `"${type}" is not a report step.` };
  }
  const parsed = schema.safeParse(params ?? {});
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    return {
      ok: false,
      path: issue?.path.join('.') || '(params)',
      message: issue?.message ?? 'Invalid params.',
    };
  }
  return {
    ok: true,
    value: { type, params: parsed.data } as NovaReportStepParams,
  };
}

/** A link the reader treats as a PDF (read one by one, not as a page batch). */
export function isNovaPdfLink(url: string): boolean {
  try {
    return new URL(url).pathname.toLowerCase().endsWith('.pdf');
  } catch {
    return false;
  }
}

/**
 * Provider read calls one `nova.read_sources` step makes: one batch for the
 * pages, one per PDF link, one for the repository, one per uploaded PDF and one
 * for a skill.
 */
export function countNovaReadCalls(params: NovaReadSourcesParams): number {
  const urls = params.urls ?? [];
  const pdfLinks = urls.filter(isNovaPdfLink).length;
  const pages = urls.length - pdfLinks;
  return (
    (pages > 0 ? 1 : 0) +
    pdfLinks +
    (params.github ? 1 : 0) +
    (params.asset_ids?.length ?? 0) +
    (params.skill_id ? 1 : 0)
  );
}

/** Research and read calls a whole plan makes per run (search + reads). */
export function countNovaResearchCalls(
  steps: ReadonlyArray<{ type: string; params?: unknown }>,
): number {
  let calls = 0;
  for (const step of steps) {
    if (step.type === NOVA_REPORT_STEP_TYPES.researchWeb) {
      calls += 1;
    } else if (step.type === NOVA_REPORT_STEP_TYPES.readSources) {
      const read = readNovaReportStepParams(step.type, step.params);
      if (read.ok && read.value.type === 'nova.read_sources') {
        calls += countNovaReadCalls(read.value.params);
      }
    }
  }
  return calls;
}

const reportOutputParamsSchema = z.object({
  research: z.enum(['on', 'off']),
  image: z.enum(['on', 'off']),
  format: z.enum(['summary', 'document', 'slides']),
});

/** The output type that carries a research plan's capability decision. */
export const NOVA_REPORT_OUTPUT_TYPE = 'report';

const FIRST_PARTY_STEP_RE = /^(nova|tabbio)\.[a-z0-9_]+$/;
const CRON_RE =
  /^(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)$/; // 5-field; deep-validated below

/** True when a step type takes a side-effecting / write action (default-deny). */
export function isNovaSideEffectingStep(type: string): boolean {
  if (type === NOVA_CONNECTOR_STEP_TYPE) return true;
  return !NOVA_READ_ONLY_STEP_TYPES.has(type);
}

// ── The catalog the planner is bound to ────────────────────────────────────────

/** Minimal connector-catalog shape the validator needs (live, from M3). */
export interface NovaConnectorCatalogEntry {
  slug: string;
  /** 'available' | 'coming_soon' | 'disabled': only 'available' is usable. */
  status: string;
  supportedActionTypes: string[];
  supportedTriggerTypes: string[];
}

// ── Zod schema (structural; deep policy is in validateNovaPlan) ────────────────

const safetySchema = z.object({
  dry_run: z.boolean(),
  requires_approval: z.boolean(),
});

const retrySchema = z.object({
  max_attempts: z.number().int().min(0).max(10),
  initial_delay_seconds: z.number().min(0).max(86_400),
  backoff_factor: z.number().min(1).max(10),
});

const stepSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  // params is intentionally permissive at the zod layer (LLM output); the deep
  // catalog-binding checks run in validateNovaPlan.
  params: z.record(z.unknown()).default({}),
  // safety MAY be omitted by the model; enforceNovaSafetyDefaults fills it and
  // validateNovaPlan asserts the side-effecting policy.
  safety: safetySchema.optional(),
  depends_on: z.array(z.string()).default([]),
  retry: retrySchema.nullish(),
});

const outputSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  destination: z.string().nullish(),
  params: z.record(z.unknown()).optional(),
});

const limitsSchema = z.object({
  max_runs_per_day: z.number().int(),
  max_concurrent_runs: z.number().int(),
});

const triggerSchema = z.object({
  type: z.enum(['cron', 'event', 'condition']),
  params: z.record(z.unknown()),
});

const metadataSchema = z.object({
  automation_id: z.string().min(1),
  version: z.number().int().min(1),
  owner_user_id: z.string().min(1),
  status: z.enum(['draft', 'active', 'paused', 'archived']),
});

/** The planner-emitted draft (NO metadata: the server attaches it). */
export const novaPlanDraftSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  summary: z.string().min(1).max(2000).optional(),
  trigger: triggerSchema,
  steps: z.array(stepSchema).min(1).max(NOVA_MAX_STEPS),
  outputs: z.array(outputSchema).default([]),
  limits: limitsSchema,
});

/** The full, canonical automation DSL (draft + server-controlled metadata). */
export const novaAutomationSchema = novaPlanDraftSchema.extend({
  metadata: metadataSchema,
});

export type NovaPlanDraftParsed = z.infer<typeof novaPlanDraftSchema>;

// ── Fixed JSON Schema (documentation + external validators) ─────────────────────
//
// A hand-authored, stable JSON Schema mirror of `novaAutomationSchema`. The zod
// schema above is the executable validator; this constant is the fixed contract
// (servable to clients / used by external tools). Keep the two in lockstep.

export const novaAutomationJsonSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  $id: 'https://tabbio.com/schemas/nova-automation-v1.json',
  title: 'NovaAutomation',
  type: 'object',
  required: ['trigger', 'steps', 'outputs', 'metadata', 'limits'],
  additionalProperties: true,
  properties: {
    name: { type: 'string', minLength: 1, maxLength: 120 },
    summary: { type: 'string', minLength: 1, maxLength: 2000 },
    trigger: {
      type: 'object',
      required: ['type', 'params'],
      properties: {
        type: { enum: ['cron', 'event', 'condition'] },
        params: { type: 'object' },
      },
    },
    steps: {
      type: 'array',
      minItems: 1,
      maxItems: NOVA_MAX_STEPS,
      items: {
        type: 'object',
        required: ['id', 'type', 'params', 'safety', 'depends_on'],
        properties: {
          id: { type: 'string', minLength: 1 },
          type: { type: 'string', minLength: 1 },
          params: { type: 'object' },
          safety: {
            type: 'object',
            required: ['dry_run', 'requires_approval'],
            properties: {
              dry_run: { type: 'boolean' },
              requires_approval: { type: 'boolean' },
            },
          },
          depends_on: { type: 'array', items: { type: 'string' } },
          retry: {
            type: ['object', 'null'],
            properties: {
              max_attempts: { type: 'integer', minimum: 0, maximum: 10 },
              initial_delay_seconds: {
                type: 'number',
                minimum: 0,
                maximum: 86400,
              },
              backoff_factor: { type: 'number', minimum: 1, maximum: 10 },
            },
          },
        },
      },
    },
    outputs: {
      type: 'array',
      items: {
        type: 'object',
        required: ['id', 'type'],
        properties: {
          id: { type: 'string', minLength: 1 },
          type: { type: 'string', minLength: 1 },
          destination: { type: ['string', 'null'] },
          params: { type: 'object' },
        },
      },
    },
    limits: {
      type: 'object',
      required: ['max_runs_per_day', 'max_concurrent_runs'],
      properties: {
        max_runs_per_day: {
          type: 'integer',
          minimum: NOVA_LIMITS_BOUNDS.maxRunsPerDay.min,
          maximum: NOVA_LIMITS_BOUNDS.maxRunsPerDay.max,
        },
        max_concurrent_runs: {
          type: 'integer',
          minimum: NOVA_LIMITS_BOUNDS.maxConcurrentRuns.min,
          maximum: NOVA_LIMITS_BOUNDS.maxConcurrentRuns.max,
        },
      },
    },
    metadata: {
      type: 'object',
      required: ['automation_id', 'version', 'owner_user_id', 'status'],
      properties: {
        automation_id: { type: 'string', minLength: 1 },
        version: { type: 'integer', minimum: 1 },
        owner_user_id: { type: 'string', minLength: 1 },
        status: { enum: ['draft', 'active', 'paused', 'archived'] },
      },
    },
  },
} as const;

// ── Safety coercion ────────────────────────────────────────────────────────────

/**
 * Returns a CLONE of the plan with safety defaults FORCED: every side-effecting
 * step is set to `dry_run: true` + `requires_approval: true`; read-only steps get
 * `{ dry_run: false, requires_approval: false }` when absent. This is the
 * server-side "FORCE" step: the planner is also prompted to set them, but this
 * guarantees the stored DSL is safe regardless of model output.
 */
export function enforceNovaSafetyDefaults<
  T extends { steps: Array<Partial<NovaStep>> },
>(plan: T): T {
  return {
    ...plan,
    steps: plan.steps.map((step) => {
      const type = String(step.type ?? '');
      const sideEffecting = isNovaSideEffectingStep(type);
      const existing = step.safety;
      return {
        ...step,
        depends_on: Array.isArray(step.depends_on) ? step.depends_on : [],
        params: step.params ?? {},
        safety: sideEffecting
          ? { dry_run: true, requires_approval: true }
          : {
              dry_run: existing?.dry_run ?? false,
              requires_approval: existing?.requires_approval ?? false,
            },
      };
    }),
  };
}

// ── Validation result ──────────────────────────────────────────────────────────

export interface NovaPlanValidationError {
  path: string;
  code: string;
  message: string;
}

export type NovaPlanValidationResult =
  | { ok: true; plan: NovaAutomation }
  | { ok: false; errors: NovaPlanValidationError[] };

interface ValidateOptions {
  catalog: NovaConnectorCatalogEntry[];
}

// ── Cron range validation ──────────────────────────────────────────────────────
//
// A 5-field cron must be range-valid, not merely character-shaped: a field like
// "99" or "FOO" is rejected here so an out-of-range schedule can never reach the
// engine (which would throw at schedules.create and silently degrade). Each field
// supports `*`, single values, ranges `a-b`, step `*/n` or `a-b/n`, and comma
// lists of those; month/day-of-week additionally accept the standard 3-letter
// names. Day-of-month and day-of-week also accept `?` (Quartz-style wildcard).

const CRON_MONTH_NAMES = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
];
const CRON_DOW_NAMES = [
  'sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat',
];

interface CronFieldBounds {
  min: number;
  max: number;
  /** name → numeric value (e.g. jan → 1, sun → 0). */
  names?: Record<string, number>;
  /** Allow the Quartz `?` wildcard (day-of-month / day-of-week only). */
  allowQuestion?: boolean;
}

const CRON_FIELD_BOUNDS: CronFieldBounds[] = [
  { min: 0, max: 59 }, // minute
  { min: 0, max: 23 }, // hour
  { min: 1, max: 31, allowQuestion: true }, // day of month
  {
    min: 1,
    max: 12,
    names: Object.fromEntries(CRON_MONTH_NAMES.map((n, i) => [n, i + 1])),
  }, // month (JAN=1 … DEC=12)
  {
    min: 0,
    max: 7, // 0 and 7 both mean Sunday
    names: Object.fromEntries(CRON_DOW_NAMES.map((n, i) => [n, i])),
    allowQuestion: true,
  }, // day of week (SUN=0 … SAT=6)
];

/** Resolve a single cron token (number or name) to its numeric value, or null. */
function resolveCronValue(token: string, bounds: CronFieldBounds): number | null {
  if (token === '') return null;
  let value: number;
  if (/^\d+$/.test(token)) {
    value = Number(token);
  } else if (bounds.names) {
    const named = bounds.names[token.toLowerCase()];
    if (named === undefined) return null;
    value = named;
  } else {
    return null;
  }
  return value >= bounds.min && value <= bounds.max ? value : null;
}

/** Validate one comma-separated cron part (single, range, or step form). */
function isValidCronPart(part: string, bounds: CronFieldBounds): boolean {
  let base = part;
  const slashIndex = part.indexOf('/');
  if (slashIndex !== -1) {
    base = part.slice(0, slashIndex);
    const step = part.slice(slashIndex + 1);
    if (!/^\d+$/.test(step) || Number(step) < 1) return false;
  }
  if (base === '*') return true;
  const dashIndex = base.indexOf('-');
  if (dashIndex !== -1) {
    const lo = resolveCronValue(base.slice(0, dashIndex), bounds);
    const hi = resolveCronValue(base.slice(dashIndex + 1), bounds);
    return lo !== null && hi !== null && lo <= hi;
  }
  return resolveCronValue(base, bounds) !== null;
}

/** Validate one cron field against its bounds (handles `*`, `?`, and lists). */
function isValidCronField(field: string, bounds: CronFieldBounds): boolean {
  if (field === '*') return true;
  if (bounds.allowQuestion && field === '?') return true;
  return field.split(',').every((part) => isValidCronPart(part, bounds));
}

function isValidCronExpression(expr: string): boolean {
  if (typeof expr !== 'string') return false;
  const m = CRON_RE.exec(expr.trim());
  if (!m) return false;
  const fields = m.slice(1, 6);
  return fields.every((field, index) =>
    isValidCronField(field, CRON_FIELD_BOUNDS[index]),
  );
}

/**
 * Validate a full NovaAutomation DSL (after `metadata` is attached + safety is
 * coerced). Returns a discriminated result: `{ ok: true, plan }` or
 * `{ ok: false, errors }` with one entry per violation (path + code + message).
 */
export function validateNovaPlan(
  input: unknown,
  options: ValidateOptions,
): NovaPlanValidationResult {
  const parsed = novaAutomationSchema.safeParse(input);
  if (!parsed.success) {
    return {
      ok: false,
      errors: parsed.error.issues.map((issue) => ({
        path: issue.path.join('.') || '(root)',
        code: 'schema',
        message: issue.message,
      })),
    };
  }

  const plan = parsed.data as unknown as NovaAutomation;
  const errors: NovaPlanValidationError[] = [];
  const available = new Map<string, NovaConnectorCatalogEntry>();
  for (const entry of options.catalog) {
    if (entry.status === 'available') available.set(entry.slug, entry);
  }

  // ── Trigger ──────────────────────────────────────────────────────────────
  validateTrigger(
    plan.trigger.type,
    plan.trigger.params as unknown as Record<string, unknown>,
    available,
    errors,
  );

  // ── Limits within bounds ─────────────────────────────────────────────────
  const { maxRunsPerDay, maxConcurrentRuns } = NOVA_LIMITS_BOUNDS;
  if (
    plan.limits.max_runs_per_day < maxRunsPerDay.min ||
    plan.limits.max_runs_per_day > maxRunsPerDay.max
  ) {
    errors.push({
      path: 'limits.max_runs_per_day',
      code: 'out_of_bounds',
      message: `max_runs_per_day must be between ${maxRunsPerDay.min} and ${maxRunsPerDay.max}.`,
    });
  }
  if (
    plan.limits.max_concurrent_runs < maxConcurrentRuns.min ||
    plan.limits.max_concurrent_runs > maxConcurrentRuns.max
  ) {
    errors.push({
      path: 'limits.max_concurrent_runs',
      code: 'out_of_bounds',
      message: `max_concurrent_runs must be between ${maxConcurrentRuns.min} and ${maxConcurrentRuns.max}.`,
    });
  }

  // ── Steps ─────────────────────────────────────────────────────────────────
  const stepIds = new Set<string>();
  for (const step of plan.steps) {
    if (stepIds.has(step.id)) {
      errors.push({
        path: `steps.${step.id}`,
        code: 'duplicate_step_id',
        message: `Duplicate step id "${step.id}".`,
      });
    }
    stepIds.add(step.id);
  }

  for (const step of plan.steps) {
    const path = `steps.${step.id}`;

    // (a) step type valid + catalog-bound.
    if (step.type === NOVA_CONNECTOR_STEP_TYPE) {
      validateConnectorStep(step, path, available, errors);
    } else if (isNovaReportStepType(step.type)) {
      const read = readNovaReportStepParams(step.type, step.params);
      if (!read.ok) {
        errors.push({
          path: `${path}.params.${read.path}`,
          code: 'invalid_step_params',
          message: `Step "${step.id}": ${read.message}`,
        });
      }
    } else if (FIRST_PARTY_STEP_RE.test(step.type)) {
      // First-party nova.*/tabbio.* steps are accepted (the executor is the
      // authority on which it implements: M4.2).
    } else {
      errors.push({
        path: `${path}.type`,
        code: 'unsupported_step_type',
        message: `Unsupported step type "${step.type}". Expected "${NOVA_CONNECTOR_STEP_TYPE}", "nova.<verb>", or "tabbio.<verb>".`,
      });
    }

    // (b) SAFETY POLICY: every side-effecting step MUST be dry_run +
    // requires_approval. This is the hard wall against auto-submit (Phase 1).
    if (isNovaSideEffectingStep(step.type)) {
      const safety = step.safety;
      if (!safety || safety.dry_run !== true || safety.requires_approval !== true) {
        errors.push({
          path: `${path}.safety`,
          code: 'side_effecting_requires_safety',
          message: `Side-effecting step "${step.id}" must set dry_run:true and requires_approval:true.`,
        });
      }
    }

    // (c) depends_on references resolve to real step ids.
    for (const dep of step.depends_on) {
      if (dep === step.id) {
        errors.push({
          path: `${path}.depends_on`,
          code: 'self_dependency',
          message: `Step "${step.id}" cannot depend on itself.`,
        });
      } else if (!stepIds.has(dep)) {
        errors.push({
          path: `${path}.depends_on`,
          code: 'unknown_dependency',
          message: `Step "${step.id}" depends on unknown step "${dep}".`,
        });
      }
    }
  }

  validateReportPlan(plan, errors);

  // (d) no cycle in depends_on.
  if (hasDependencyCycle(plan.steps)) {
    errors.push({
      path: 'steps',
      code: 'dependency_cycle',
      message: 'Step dependencies contain a cycle.',
    });
  }

  if (errors.length) {
    return { ok: false, errors };
  }
  return { ok: true, plan };
}

/**
 * Plan-level rules for research and report steps: a report needs something to
 * read, a render needs a document or slides report, delivery needs a report,
 * the per-run research call cap holds, and the `report` output carries a
 * well-formed capability decision.
 */
function validateReportPlan(
  plan: NovaAutomation,
  errors: NovaPlanValidationError[],
): void {
  const types = plan.steps.map((step) => step.type);
  const has = (type: string) => types.includes(type);
  const reportSteps = plan.steps.filter((step) =>
    isNovaReportStepType(step.type),
  );
  if (!reportSteps.length) {
    for (const output of plan.outputs) {
      if (output.type === NOVA_REPORT_OUTPUT_TYPE) {
        errors.push({
          path: `outputs.${output.id}`,
          code: 'report_output_without_report',
          message: 'A report output needs a compose_report step.',
        });
      }
    }
    return;
  }

  const reads =
    has(NOVA_REPORT_STEP_TYPES.researchWeb) ||
    has(NOVA_REPORT_STEP_TYPES.readSources);
  const composes = plan.steps.filter(
    (step) => step.type === NOVA_REPORT_STEP_TYPES.composeReport,
  );
  if (composes.length && !reads) {
    errors.push({
      path: 'steps',
      code: 'report_without_sources',
      message: 'A compose_report step needs a research_web or read_sources step before it.',
    });
  }
  if (composes.length > 1) {
    errors.push({
      path: 'steps',
      code: 'too_many_reports',
      message: 'A plan composes at most one report.',
    });
  }
  const compose = composes[0];
  const composeParams = compose
    ? readNovaReportStepParams(compose.type, compose.params)
    : null;
  const format =
    composeParams?.ok && composeParams.value.type === 'nova.compose_report'
      ? composeParams.value.params.format
      : null;
  if (has(NOVA_REPORT_STEP_TYPES.renderDocument)) {
    if (!compose) {
      errors.push({
        path: 'steps',
        code: 'render_without_report',
        message: 'A render_document step needs a compose_report step.',
      });
    } else if (format === 'summary') {
      errors.push({
        path: 'steps',
        code: 'render_summary',
        message: 'A summary is not rendered: drop render_document or pick document or slides.',
      });
    }
  }
  if (has(NOVA_REPORT_STEP_TYPES.deliver) && !compose) {
    errors.push({
      path: 'steps',
      code: 'deliver_without_report',
      message: 'A deliver step needs a compose_report step.',
    });
  }
  if ((reads || has(NOVA_REPORT_STEP_TYPES.renderDocument)) && !compose) {
    // Research without a report has nothing to show the user.
    if (!errors.some((error) => error.code === 'render_without_report')) {
      errors.push({
        path: 'steps',
        code: 'research_without_report',
        message: 'Research steps need a compose_report step to turn them into a result.',
      });
    }
  }

  const calls = countNovaResearchCalls(plan.steps);
  if (calls > NOVA_REPORT_LIMITS.researchCallsPerRun) {
    errors.push({
      path: 'steps',
      code: 'too_many_research_calls',
      message: `A run may make at most ${NOVA_REPORT_LIMITS.researchCallsPerRun} research or read calls (this plan makes ${calls}).`,
    });
  }

  for (const output of plan.outputs) {
    if (output.type !== NOVA_REPORT_OUTPUT_TYPE) continue;
    const parsed = reportOutputParamsSchema.safeParse(output.params ?? {});
    if (!parsed.success) {
      errors.push({
        path: `outputs.${output.id}.params`,
        code: 'invalid_report_output',
        message: 'A report output carries { research: on|off, image: on|off, format }.',
      });
    }
  }
}

function validateTrigger(
  type: NovaTriggerType,
  params: Record<string, unknown>,
  available: Map<string, NovaConnectorCatalogEntry>,
  errors: NovaPlanValidationError[],
): void {
  if (type === 'cron') {
    const expr = params.cron_expression;
    if (typeof expr !== 'string' || !isValidCronExpression(expr)) {
      errors.push({
        path: 'trigger.params.cron_expression',
        code: 'invalid_cron',
        message: 'cron_expression must be a valid 5-field cron expression.',
      });
    }
    if (typeof params.timezone !== 'string' || !params.timezone.trim()) {
      errors.push({
        path: 'trigger.params.timezone',
        code: 'invalid_timezone',
        message: 'A timezone is required for a cron trigger.',
      });
    }
  } else if (type === 'event') {
    const source = params.source;
    if (typeof source !== 'string' || !available.has(source)) {
      errors.push({
        path: 'trigger.params.source',
        code: 'unknown_event_source',
        message: `Event source must be a connected, available service from the catalog (got "${String(source)}").`,
      });
    } else {
      const connector = available.get(source)!;
      const eventType = params.event_type;
      if (
        typeof eventType !== 'string' ||
        !connector.supportedTriggerTypes.includes(eventType)
      ) {
        errors.push({
          path: 'trigger.params.event_type',
          code: 'unsupported_event_type',
          message: `"${source}" does not support event "${String(eventType)}".`,
        });
      }
    }
  } else if (type === 'condition') {
    const expr = params.poll_cron_expression;
    if (typeof expr !== 'string' || !isValidCronExpression(expr)) {
      errors.push({
        path: 'trigger.params.poll_cron_expression',
        code: 'invalid_cron',
        message: 'poll_cron_expression must be a valid 5-field cron expression.',
      });
    }
    if (
      typeof params.condition_expression !== 'string' ||
      !params.condition_expression.trim()
    ) {
      errors.push({
        path: 'trigger.params.condition_expression',
        code: 'invalid_condition',
        message: 'A condition_expression is required for a condition trigger.',
      });
    }
  }
}

function validateConnectorStep(
  step: NovaStep,
  path: string,
  available: Map<string, NovaConnectorCatalogEntry>,
  errors: NovaPlanValidationError[],
): void {
  const params = step.params as Record<string, unknown>;
  const connectorSlug = params.connector;
  const action = params.action;
  if (typeof connectorSlug !== 'string' || !available.has(connectorSlug)) {
    errors.push({
      path: `${path}.params.connector`,
      code: 'unknown_connector',
      message: `Step "${step.id}" references unknown/unavailable connector "${String(connectorSlug)}".`,
    });
    return;
  }
  const connector = available.get(connectorSlug)!;
  if (
    typeof action !== 'string' ||
    !connector.supportedActionTypes.includes(action)
  ) {
    errors.push({
      path: `${path}.params.action`,
      code: 'unsupported_action',
      message: `"${connectorSlug}" does not support action "${String(action)}".`,
    });
  }
}

/** DFS cycle detection over the step dependency graph. */
function hasDependencyCycle(steps: NovaStep[]): boolean {
  const graph = new Map<string, string[]>();
  for (const step of steps) {
    graph.set(step.id, step.depends_on ?? []);
  }
  const WHITE = 0;
  const GRAY = 1;
  const BLACK = 2;
  const color = new Map<string, number>();
  for (const id of graph.keys()) color.set(id, WHITE);

  const visit = (id: string): boolean => {
    color.set(id, GRAY);
    for (const dep of graph.get(id) ?? []) {
      if (!graph.has(dep)) continue; // unknown dep already reported
      const c = color.get(dep);
      if (c === GRAY) return true;
      if (c === WHITE && visit(dep)) return true;
    }
    color.set(id, BLACK);
    return false;
  };

  for (const id of graph.keys()) {
    if (color.get(id) === WHITE && visit(id)) return true;
  }
  return false;
}
